'use client';

import React, { useState } from 'react';

import Link from 'next/link';

import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { visuallyHidden } from '@mui/utils';

import { InfoCard } from '@/components/cards/InfoCard';

/** Describes one fixed welcome tip rendered by the homepage carousel. */
export interface HomepageWelcomeCarouselItem {
  text: React.ReactNode;
}

/** Supplies the welcome tips and dismissal state for the homepage carousel. */
interface HomepageWelcomeCarouselCardProps {
  items: HomepageWelcomeCarouselItem[];
  onDismiss: () => Promise<void> | void;
  isDismissing: boolean;
}

/** Renders the source-authoritative welcome tips carousel presentation. */
export const HomepageWelcomeCarouselCard: React.FC<HomepageWelcomeCarouselCardProps> = ({
  items,
  onDismiss,
  isDismissing,
}) => {
  /** Tracks the currently visible welcome tip. */
  const [activeIndex, setActiveIndex] = useState(0);

  if (items.length === 0) {
    return null;
  }

  const displayedIndex = activeIndex < items.length ? activeIndex : 0;
  const activeItem = items[displayedIndex];

  /** Reserves matching numeric columns sized for the largest possible position. */
  const countColumnWidth = `${String(items.length).length}ch`;

  /** Moves backward with wraparound from the first tip to the last. */
  const goPrev = () => {
    setActiveIndex((displayedIndex - 1 + items.length) % items.length);
  };

  /** Moves forward with wraparound from the last tip back to the first. */
  const goNext = () => {
    setActiveIndex((displayedIndex + 1) % items.length);
  };

  return (
    <InfoCard
      title="Welcome tips"
      headerAction={
        items.length > 1 ? (
          <Stack
            component="nav"
            aria-label="Welcome tip navigation"
            direction="row"
            alignItems="center"
            spacing={0.5}
          >
            <IconButton aria-label="Previous welcome tip" onClick={goPrev} size="small">
              <ChevronLeftIcon />
            </IconButton>
            <Box role="status" sx={visuallyHidden}>
              Welcome tip {displayedIndex + 1} of {items.length}
            </Box>
            <Typography
              component="div"
              variant="caption"
              color="text.secondary"
              aria-hidden="true"
              data-testid="welcome-tip-position"
              sx={{
                display: 'grid',
                gridTemplateColumns: `${countColumnWidth} auto ${countColumnWidth}`,
                columnGap: 0.5,
                alignItems: 'baseline',
                fontVariantNumeric: 'tabular-nums',
              }}
            >
              <Box component="span" textAlign="right">
                {displayedIndex + 1}
              </Box>
              <Box component="span">/</Box>
              <Box component="span" textAlign="left">
                {items.length}
              </Box>
            </Typography>
            <IconButton aria-label="Next welcome tip" onClick={goNext} size="small">
              <ChevronRightIcon />
            </IconButton>
          </Stack>
        ) : null
      }
      testId="home-welcome-carousel"
      actions={
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          alignItems={{ xs: 'stretch', sm: 'center' }}
          justifyContent="space-between"
          spacing={1}
          sx={{ width: '100%' }}
        >
          <Button component={Link} href="/about/help" variant="outlined" size="small">
            View Help Guide
          </Button>

          <Button onClick={onDismiss} variant="contained" disabled={isDismissing}>
            {isDismissing ? 'Dismissing...' : 'Dismiss all tips'}
          </Button>
        </Stack>
      }
      sx={{ mb: 3 }}
    >
      <Typography variant="body1" color="text.primary" sx={{ whiteSpace: 'pre-line' }}>
        {activeItem.text}
      </Typography>
    </InfoCard>
  );
};
